import { GetStaticPaths, GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import { PHASE_DEVELOPMENT_SERVER, PHASE_PRODUCTION_BUILD } from 'next/constants';
import { useTranslation } from 'next-i18next';
import { Store, WithContext } from 'schema-dts';

import { CHANNEL, storePageSlugs } from '@boss/constants/b2b-b2c';
import { contentfulContext } from '@boss/contentful-client';
import { getServicePoint } from '@boss/contentful-client/b2b-b2c';
import { events as eventService, store as storeService } from '@boss/services';
import { Environment } from '@boss/types/b2b-b2c';
import {
  Cta,
  DetailInformationCard,
  ImageWithCaption,
  OpeningHours,
  ServicesBlock,
  SpecialMessageNotice,
} from '@boss/ui';
import {
  getCurrentOpeningHours,
  getTranslatedSlugsByType,
  openinghoursToStructuredString,
  shopIsOpen,
  slugify,
} from '@boss/utils';

import {
  ComponentMapper,
  EventCardWrapper,
  SEO,
  getImageProps,
  getPromotionCtaProps,
  getServiceLinks,
  getServicePromotionCta,
  getSpecialMessage,
} from '../../components';
import { PageTypes } from '../../constants';
import Layout from '../../layout/default';
import { handleStores, isB2b } from '../../utils';
import getServerSideTranslations from '../../utils/getServerSideTranslations';
import {
  mapContentfulToNextLocale,
  mapNextToContentfulLocaleMarket,
  mapNextToContentfulMarket,
} from '../../utils/localeMapper';
import { getMappedNavigation } from '../../utils/navigation';
import { storeConfig, storeDetailTheme, storeFinderVariant } from '../../utils/options';

export const config = {
  maxDuration: 30,
};

const IS_BUILD =
  (process.env.NEXT_PHASE === PHASE_PRODUCTION_BUILD && !process.env.SKIP_PRODUCT_BUILD) ||
  (process.env.NEXT_PHASE === PHASE_DEVELOPMENT_SERVER && process.env.DEBUG_BUILD === 'true');

const SKEDIFY_CONFIG_SERVICE_APP = {
  intent: 'schedule',
  flow: 'TQC',
  'subject-type': 'externalId',
  'subject-ids': 'AKL',
  'office-type': 'externalId',
  'office-ids': '',
  'session-status': 'disabled',
};

const SKEDIFY_CONFIG_PAINTER_APP = {
  intent: 'schedule',
  flow: 'TQC',
  'subject-type': 'externalId',
  'subject-ids': 'ASCH',
  'office-type': 'externalId',
  'office-ids': '',
  'session-status': 'disabled',
};

/**
 * This function is given by the Skedify docs for encoding the config object to a BASE64 url param
 * Source: https://colora.plugin.skedify.io/docs/docs/faq#safe-binary-encoding
 */
const encodeSkedifyConfig = (config: Record<string, string>) => {
  const str = JSON.stringify(config);
  const hash = btoa(
    encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, function (_, p1) {
      return String.fromCharCode(parseInt(p1, 16));
    }),
  );

  return '#pexip-' + hash;
};

/**
 * To navigate to a prefilled skedify form we need to extend the service href with an uri hash
 * The data coming from CF is maxed out at two entries
 */
const extendServiceLinksWithSkedifyHash = (serviceLinks: ReturnType<typeof getServiceLinks>, storeId: string) =>
  serviceLinks?.map((service, i) => ({
    ...service,
    href:
      service.href +
      encodeSkedifyConfig({
        ...(i === 0 ? SKEDIFY_CONFIG_SERVICE_APP : SKEDIFY_CONFIG_PAINTER_APP),
        'office-ids': storeId,
      }),
  }));

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  if (!locales) {
    return {
      paths: [],
      fallback: 'blocking',
    };
  }

  const slugsPromises = locales
    .filter(locale => locale !== 'default')
    .map(async locale => {
      const stores = await storeService.getAllStores({ locale });

      return handleStores(stores, storeConfig).map(storeData => ({
        params: { slug: slugify(storeData.name) },
        locale: mapContentfulToNextLocale(locale),
      }));
    });

  const slugs = await Promise.all(slugsPromises);

  return {
    paths: slugs.flat(),
    fallback: 'blocking',
  };
};

export const getStaticProps = async ({ preview, locale, params }: GetStaticPropsContext) => {
  const slug = params?.slug;

  if (!slug || Array.isArray(slug) || !locale || locale === 'default') {
    return {
      notFound: true,
    };
  }

  const contentfulLocaleMarket = mapNextToContentfulLocaleMarket(locale);
  const market = mapNextToContentfulMarket(locale);

  if (!contentfulLocaleMarket || !market) {
    return {
      notFound: true,
    };
  }

  // SET GLOBAL MARKET CONTEXT
  contentfulContext.market = market;

  const [storeData, workshops] = await Promise.all([
    storeService.getStoreByStoreName(slugify(slug), locale),
    !isB2b ? eventService.getWorkshops(locale, IS_BUILD) : [],
  ]);

  if (!storeData) {
    return {
      notFound: true,
    };
  }

  const relatedWorkshops = workshops.filter(workshop => workshop.locationId === storeData?.id);

  const [navigation, servicePoint] = await Promise.all([
    getMappedNavigation(contentfulLocaleMarket, !!preview),
    getServicePoint({
      preview: !!preview,
      locale: contentfulLocaleMarket,
      market,
      serviceID: storeData.id,
      channel: CHANNEL,
    }),
  ]);

  return {
    props: {
      pageType: PageTypes.STORE_DETAIL,
      storeData: storeData,
      servicePointData: servicePoint,
      slug,
      locale,
      preview: !!preview,
      navigation,
      workshops: relatedWorkshops,
      environment: process.env.CONTENTFUL_ENVIRONMENT as Environment,
      ...(await getServerSideTranslations(locale, ['common', 'stores', 'routes', 'forms', 'event'])),
    },
  };
};

export const Index = ({
  slug,
  navigation,
  preview,
  locale,
  environment,
  storeData,
  servicePointData,
  workshops,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { t } = useTranslation();
  const isColora = CHANNEL === 'colora';
  const localizedSlugs = getTranslatedSlugsByType(slug, 'Store overview');
  const pageTitle = storeData.name;
  const pageDescription = pageTitle; // TODO: Update with final value once this is known
  const crumbles = [
    { href: storePageSlugs[locale], label: t('storesLabel', { ns: 'common' }) },
    { href: slug, label: pageTitle },
  ];
  const shopDetailsTranslations = t('shopDetails', { ns: 'stores', returnObjects: true });
  const servicePointCtaData = getServicePromotionCta(servicePointData);
  const servicePointImage = servicePointData && getImageProps(servicePointData?.images[0]);
  const skedifyButtonProps = extendServiceLinksWithSkedifyHash(
    getServiceLinks(servicePointData, storeDetailTheme, locale),
    storeData.id,
  );

  const structuredData: WithContext<Store>[] = [
    {
      '@context': 'https://schema.org',
      '@type': 'Store',
      name: storeData.name,
      telephone: storeData.phone,
      latitude: storeData.latitude,
      longitude: storeData.longitude,
      address: Object.values(storeData.address).join(' '),
      openingHours: openinghoursToStructuredString(storeData.openinghours),
      email: storeData.email,
      branchCode: storeData.id,
      keywords: `${storeData.storetype} ${storeData.availableservices.join(' ')}`,
    },
  ];

  return (
    <SEO
      breadcrumbs={crumbles}
      description={pageDescription}
      localizedSlugs={localizedSlugs}
      navigation={navigation}
      structuredData={structuredData}
      title={pageTitle}
    >
      <Layout
        crumbles={crumbles}
        environment={environment}
        localizedSlugs={localizedSlugs}
        navigation={navigation}
        preview={preview}
      >
        <div className="mb-12 grid w-full grid-cols-1 md:grid-cols-2 xl:grid-cols-5 xl:gap-8">
          <div className="flex flex-col gap-y-6 md:col-span-2 xl:col-span-3">
            <DetailInformationCard
              ShopDetailTranslations={t('shopDetails', { ns: 'stores', returnObjects: true })}
              address={storeData.address}
              currentOpeningHours={getCurrentOpeningHours(storeData)}
              email={storeData.email}
              isOpen={shopIsOpen(storeData)}
              tel={storeData.phone}
              title={storeData.name}
              variant={storeFinderVariant}
            />
            <div className="grid-col-1 md:grid-col-2 mb-6 grid gap-y-6 md:grid-cols-9 md:gap-4">
              <div className="col-span-5 pl-5 md:flex md:flex-col md:gap-y-4 md:pl-0">
                <OpeningHours
                  defaults={storeData.openinghours.defaults}
                  exceptions={storeData.openinghours.exceptions}
                  translations={t('openingHours', { ns: 'stores', returnObjects: true })}
                />

                {isColora && servicePointData?.facebookLink && (
                  <Cta
                    className="hidden md:flex"
                    href={servicePointData.facebookLink}
                    icon="facebook"
                    label={t('facebookLink')}
                    type="inline"
                  />
                )}
              </div>
              {servicePointData?.specialMessage && (
                <div className="col-span-5 md:col-span-4">
                  <SpecialMessageNotice
                    image={
                      servicePointData.specialMessagesImage
                        ? getImageProps(servicePointData.specialMessagesImage)
                        : undefined
                    }
                    message={getSpecialMessage(servicePointData.specialMessage, 'Colora: pink' as const, locale)}
                    variant={storeFinderVariant}
                  />
                </div>
              )}
            </div>
          </div>
          <div className="col-span-2">
            <div className="flex flex-col">
              {servicePointData && (
                <>
                  {servicePointImage?.image?.file?.url && (
                    <ImageWithCaption
                      caption={servicePointData.imageCaption}
                      image={servicePointImage}
                      variant={storeFinderVariant}
                    />
                  )}
                  {isColora && (
                    <div className="mt-8 md:hidden">
                      <Cta
                        href={servicePointData.facebookLink}
                        icon="facebook"
                        label={t('facebookLink')}
                        type="inline"
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        {!isB2b && (
          <>
            {!!workshops?.length && (
              <div>
                <h3 className="text-lg font-bold">
                  {t('upcomingEvents', { ns: 'stores' })} {storeData.name}
                </h3>
                <div className="my-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {workshops.map(workshop => (
                    <EventCardWrapper event={workshop} key={workshop.id} locale={locale} />
                  ))}
                </div>
              </div>
            )}
            <ServicesBlock
              buttons={skedifyButtonProps}
              promotionCta={
                servicePointCtaData ? getPromotionCtaProps(servicePointCtaData, storeDetailTheme, locale) : null
              }
              services={storeData?.availableservices}
              translations={shopDetailsTranslations}
            />
          </>
        )}
        {servicePointData?.suffixSections?.map(entry => (
          <ComponentMapper entry={entry} key={entry.id} locale={locale} />
        ))}
      </Layout>
    </SEO>
  );
};

export default Index;
